import { and, eq, not } from 'tio-ui/utilities';
import { concat } from '@ember/helper';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import { dropTask } from 'ember-concurrency';
import { fn } from '@ember/helper';
import { getSingleValueForTasField } from '../../../utils/tuition-assistance/fields.ts';
import { LinkTo } from '@ember/routing';
import * as v from 'valibot';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import errorsForField from '../../../helpers/errors-for-field.ts';
import RadioGroup from 'tio-reimagined/components/radio-group';
import Textarea from 'tio-reimagined/components/textarea';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import ConfirmationDialog from 'tio-reimagined/components/confirmation-dialog';
import ErrorMessages from 'tio-reimagined/components/error-messages';
import type {
  TasProgramInstanceState,
  TasApplicationState,
} from '../../../types/tuition-assistance';
import SelectTextArea from '../../tio/select-text-area.gts';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import { Section } from 'tio-ui/components/layout';

import type SessionContextService from '../../../services/session-context.ts';
type ActionSubMap = {
  true: string;
  false: string;
  CHANGE_REQUESTED: string;
};

type ActionMapType = {
  [key in TasProgramInstanceState | TasApplicationState]?: ActionSubMap;
};

interface TuitionAssistanceApplicationDetailsProgramStatusApprovalSignature {
  Args: {
    instance: TASProgramInstanceModel;
    routePrefix: string;
  };
}

export default class TuitionAssistanceApplicationDetailsProgramStatusApprovalComponent extends Component<TuitionAssistanceApplicationDetailsProgramStatusApprovalSignature> {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;

  @tracked customNote = '';
  @tracked approveStatusResponse: boolean | 'CHANGE_REQUESTED' | undefined = undefined;
  @tracked hasSubmitted = false;
  @tracked submitError = null;
  @tracked showConfirmApproverOverridePrompt = false;
  @tracked showConfirmSubmitPrompt = false;

  get inPendingApprovalState() {
    return !!this.args.instance.state.includes('PENDING');
  }

  get state() {
    return this.args.instance.state;
  }

  get adapterMethod() {
    const actionMap: ActionMapType = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        true: 'approveProgram',
        false: 'rejectProgram',
        CHANGE_REQUESTED: 'requestChanges',
      },
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
        true: 'approveProgramCompletion',
        false: 'rejectProgramCompletion',
        CHANGE_REQUESTED: 'requestProgramCompletionChanges',
      },
      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
        true: 'approveProgramWithdrawal',
        false: 'rejectProgramWithdrawal',
        CHANGE_REQUESTED: 'requestProgramWithdrawalChanges',
      },
    };
    const adapterActions = actionMap[this.state];
    // @ts-expect-error will need help with this one
    return adapterActions?.[this.approveStatusResponse] ?? undefined;
  }

  get cannedApproverResponse() {
    const templateFields = this.args.instance.tasProgramTemplate.fields;

    const actionMap: ActionMapType = {
      'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
        true: getSingleValueForTasField('PROGRAM_APPROVAL_APPROVE_TEXT', templateFields),
        false: getSingleValueForTasField('PROGRAM_APPROVAL_REJECT_TEXT', templateFields),
        CHANGE_REQUESTED: getSingleValueForTasField('PROGRAM_APPROVAL_REVISE_TEXT', templateFields),
      },

      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
        true: getSingleValueForTasField('WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT', templateFields),
        false: getSingleValueForTasField('WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT', templateFields),
        CHANGE_REQUESTED: getSingleValueForTasField(
          'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT',
          templateFields
        ),
      },

      'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
        true: getSingleValueForTasField('WITHDRAW_COMPLETE_APPROVAL_APPROVE_TEXT', templateFields),
        false: getSingleValueForTasField('WITHDRAW_COMPLETE_APPROVAL_REJECT_TEXT', templateFields),
        CHANGE_REQUESTED: getSingleValueForTasField(
          'WITHDRAW_COMPLETE_APPROVAL_REVISE_TEXT',
          templateFields
        ),
      },
    };

    const response = actionMap[this.state];
    // @ts-expect-error will need help with this one
    return response?.[this.approveStatusResponse] ?? '';
  }

  get isFormValid() {
    return !this.formValidationErrors.length;
  }

  get formValidationErrors() {
    if (!this.hasSubmitted) {
      return [];
    }

    const schema = v.object({
      customNote: v.pipe(v.string('Please provide a note'), v.nonEmpty('Please provide a note')),
      approvalAction: v.pipe(
        v.union([v.literal(true), v.literal(false), v.literal('CHANGE_REQUESTED')]),
        v.check((value) => value !== undefined, 'Please select an action')
      ),
    });

    const formModel = {
      customNote: this.customNote,
      approvalAction: this.approveStatusResponse,
    };

    const result = v.safeParse(schema, formModel);

    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};

      // Convert Valibot errors to format compatible with errorsForField helper (Yup format)
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: formModel[field as keyof typeof formModel] as string,
      }));
    }

    return [];
  }

  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.instance.currentExpectedApproverEmployeeIds;
    const employees = this.sessionContext.user?.person?.employees || [];

    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  get employeeHasAppsInRepayment() {
    return !!this.appsInRepayment.length;
  }

  get appsInRepayment() {
    // TODO: this will need to be adjusted to send back a total amount requested as well as id
    return this.tasApplications.reduce((acc: string[], app: TasApplicationModel) => {
      if (app.isRefundRequested) {
        // @ts-expect-error: Application id type mismatch with string array
        acc.push(app.id);
      }
      return acc;
    }, []);
  }

  tasApplicationsData = trackedFunction(this, async () => {
    if (!this.args.instance.employee.id) {
      return [];
    }

    return (await this.store.query('tas-application', {
      filter: { employee: this.args.instance?.employee?.id },
    })) as TasApplicationModel[];
  });

  get tasApplications() {
    return this.tasApplicationsData.value || [];
  }

  submitTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;

    if (this.formValidationErrors.length) {
      return;
    }
    try {
      this.submitError = null;

      await this.store
        .adapterFor('tasProgramInstance')
        [this.adapterMethod](this.args.instance, this.customNote);

      this.router.transitionTo(`${this.args.routePrefix}.tas.approval-queue`);
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  get confirmSubmitMessage() {
    if (this.approveStatusResponse === true)
      return this.intl.t('common.tuition_assistance.approval.confirm_approve');
    if (this.approveStatusResponse === false)
      return this.intl.t('common.tuition_assistance.approval.confirm_reject');
    return this.intl.t('common.tuition_assistance.approval.confirm_request_changes');
  }

  attemptSubmitApproval = () => {
    this.hasSubmitted = true;

    if (this.formValidationErrors.length) {
      return;
    }

    this.showConfirmSubmitPrompt = true;
  };

  confirmSubmitApproval = () => {
    this.showConfirmSubmitPrompt = false;

    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitTask.perform();
    }
  };

  cancelSubmitApproval = () => {
    this.showConfirmSubmitPrompt = false;
  };

  setApproveStatusResponse = (value: boolean | 'CHANGE_REQUESTED') => {
    this.approveStatusResponse = value;
    this.customNote = this.cannedApproverResponse;
  };

  get tasResponseNotes() {
    // TODO: decide if these should be filtered by response type - James 2024-06-27
    const partnerComments = this.args.instance.employee.company.comments;
    return partnerComments
      .filter((comment) => comment.isTasApproval)
      .map((comment) => comment.tasApprovalNote);
  }

  <template>
    {{#if this.inPendingApprovalState}}
      <Section>
        <:body>
          <div class="flex flex-col justify-center content-center gap-2 md:max-w-md mx-auto">
            <p class="font-semibold mb-2">
              {{t "tuition_assistance.application_details.approve_reject_application"}}
              <span class="text-error-400">*</span>
            </p>
            <RadioGroup
              data-legacy-input
              @value={{this.approveStatusResponse}}
              @onChange={{this.setApproveStatusResponse}}
              class="flex gap-8"
              @errors={{errorsForField "approvalAction" schemaErrors=this.formValidationErrors}}
              as |Radio|
            >
              <Radio @value={{true}} @label="Approve" />
              <Radio @value={{false}} @label="Reject" />
              <Radio @value="CHANGE_REQUESTED" @label="Request Change" />
            </RadioGroup>

            {{#if this.tasResponseNotes.length}}
              <SelectTextArea
                @options={{this.tasResponseNotes}}
                @optionLabel="title"
                @optionValue="body"
                @value={{this.customNote}}
                @onChange={{fn (mut this.customNote)}}
                @placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
              />
            {{else}}
              <Textarea
                @value={{this.customNote}}
                @onChange={{fn (mut this.customNote)}}
                placeholder={{t "tuition_assistance.application_details.add_a_custom_note"}}
                @errors={{errorsForField "customNote" schemaErrors=this.formValidationErrors}}
              />
            {{/if}}

            {{#if (and this.employeeHasAppsInRepayment (eq this.approveStatusResponse true))}}
              <ErrorMessages
                @showErrors={{true}}
                @error={{t "tuition_assistance.application_details.please_keep_in_mind"}}
                class="text-sm"
              />

              <div class="text-sm flex gap-4 pl-16 my-4">
                <p class="font-medium shrink-0">
                  {{t "tuition_assistance.application_details.application_id"}}
                </p>
                <div class="flex flex-wrap gap-4">
                  {{#each this.appsInRepayment as |id|}}
                    <LinkTo
                      class="underline text-ocean-600"
                      @route={{concat @routePrefix ".tuition-assistance.applications.show.index"}}
                      @model={{id}}
                    >
                      {{id}}
                    </LinkTo>
                  {{/each}}
                </div>
              </div>
            {{/if}}

            <Button
              @intent="primary"
              {{on "click" this.attemptSubmitApproval}}
              @isRunning={{this.submitTask.isRunning}}
              disabled={{not this.isFormValid}}
              class="self-center w-48"
            >
              {{t "common.submit"}}
            </Button>
          </div>

          <ConfirmationDialog
            @isOpen={{this.showConfirmSubmitPrompt}}
            @title="Confirm Submission"
            @intent="warning"
            @onConfirm={{this.confirmSubmitApproval}}
            @onCancel={{this.cancelSubmitApproval}}
            @confirmText="Yes, Submit"
            @cancelText="Cancel"
          >
            <p class="mb-2 font-semibold">
              {{this.confirmSubmitMessage}}
            </p>
          </ConfirmationDialog>

          <ConfirmationDialog
            @isOpen={{this.showConfirmApproverOverridePrompt}}
            @title="Out of Sequence Approver"
            @intent="warning"
            @onConfirm={{this.submitTask.perform}}
            @onCancel={{fn (mut this.showConfirmApproverOverridePrompt) false}}
            @confirmText="Yes"
            @cancelText="No"
          >
            <p class="mb-2 font-semibold">
              {{t "tuition_assistance.application_details.out_of_sequence_approver_warning"}}
            </p>
          </ConfirmationDialog>
        </:body>
      </Section>
    {{/if}}
  </template>
}
